// components/ui/Select.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search, X } from "lucide-react";
import { cn } from "../../../utils/cn";
import Button from "./Button";
import Input from "./Input";

const Select = React.forwardRef(
  (
    {
      isMulti,
      className,
      options = [],
      value,
      defaultValue,
      placeholder = "Select an option",
      multiple = false,
      disabled = false,
      required = false,
      label,
      description,
      error,
      searchable = false,
      clearable = false,
      loading = false,
      id,
      name,
      maxSelected,
      onChange,
      onOpenChange,
      menuPortalTarget = null,
      menuPlacement = "auto", // "auto", "top", "bottom"
      forceClose = false,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOptions, setSelectedOptions] = useState(isMulti ? [] : null);
    const [dropdownPosition, setDropdownPosition] = useState({
      top: 0,
      left: 0,
      width: 0,
      placement: "bottom",
    });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

    // Force close when parent signals another dropdown opened
    useEffect(() => {
      if (forceClose && isOpen) {
        setIsOpen(false);
        setIsClosing(false);
        setSearchTerm("");
        onOpenChange?.(false);
      }
    }, [forceClose]);

    const closeDropdown = () => {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
        setSearchTerm("");
        onOpenChange?.(false);
      }, 120);
    };

    // Update dropdown position when opened
    useEffect(() => {
      if (isOpen && buttonRef.current && menuPortalTarget) {
        const updatePosition = () => {
          const rect = buttonRef.current.getBoundingClientRect();
          const dropdownHeight = 300; // Approximate max height
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;

          let placement = menuPlacement;
          if (menuPlacement === "auto") {
            // Auto-detect: prefer bottom, but use top if not enough space below
            placement =
              spaceBelow < dropdownHeight && spaceAbove > spaceBelow
                ? "top"
                : "bottom";
          }

          setDropdownPosition({
            top:
              placement === "top"
                ? rect.top - Math.min(dropdownHeight, spaceAbove) - 4
                : rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            placement,
          });
        };

        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        return () => {
          window.removeEventListener("scroll", updatePosition, true);
          window.removeEventListener("resize", updatePosition);
        };
      }
    }, [isOpen, menuPortalTarget, menuPlacement]);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event) => {
        if (
          buttonRef.current &&
          !buttonRef.current.contains(event.target) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          closeDropdown();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const filteredOptions =
      searchable && searchTerm
        ? options?.filter((option) => {
            const term = searchTerm.toLowerCase();
            // Match against label, value, and any country/description fields so
            // searches like a country name (e.g. "india") or a dial code
            // (e.g. "91") both work. Uses includes rather than startsWith so a
            // match anywhere in the text counts.
            const haystacks = [
              option?.label,
              option?.value,
              option?.country,
              option?.description,
            ];
            return haystacks.some(
              (field) =>
                typeof field === "string" && field.toLowerCase().includes(term),
            );
          })
        : options;

    const getSelectedDisplay = () => {
      if (!value || (Array.isArray(value) && value.length === 0))
        return placeholder;

      if (isMulti) {
        if (value.length === 1) return value[0].label;
        return `${value.length} selected`;
      }

      return value.label;
    };

    const handleToggle = () => {
      if (!disabled) {
        if (isOpen) {
          closeDropdown();
        } else {
          setIsOpen(true);
          onOpenChange?.(true);
        }
      }
    };

    const handleOptionSelect = (option) => {
      if (isMulti) {
        const alreadySelected = value || [];

        const isAlreadySelected = alreadySelected.some(
          (selected) => selected.value === option.value,
        );

        let updatedOptions;
        if (isAlreadySelected) {
          updatedOptions = alreadySelected?.filter(
            (selected) => selected.value !== option.value,
          );
        } else {
          if (alreadySelected.length >= maxSelected) return;
          updatedOptions = [...alreadySelected, option];
        }

        onChange?.(updatedOptions);
      } else {
        onChange?.(option);
        closeDropdown();
      }
    };

    const handleClear = (e) => {
      e.stopPropagation();
      onChange?.(multiple ? [] : "");
    };

    const handleSearchChange = (e) => {
      setSearchTerm(e.target.value);
    };

    const isSelected = (val) => {
      if (isMulti) {
        return (value || []).some((v) => v.value === val);
      }
      return value?.value === val;
    };

    const hasValue = multiple ? value?.length > 0 : !!value;

    return (
      <div className={cn("relative ", className)}>
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              "text-sm font-medium block mb-3",
              error ? "text-destructive" : "text-foreground",
            )}
          >
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <button
            ref={(node) => {
              buttonRef.current = node;
              if (typeof ref === "function") ref(node);
              else if (ref) ref.current = node;
            }}
            id={selectId}
            type="button"
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background transition-colors duration-150 ease-out hover:border-logo2/60 disabled:cursor-not-allowed disabled:opacity-50",
              error && "border-destructive focus:ring-destructive",
              !hasValue && "text-muted-foreground",
            )}
            onClick={handleToggle}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            {...props}
          >
            <span className="truncate">{getSelectedDisplay() || value}</span>
            <div className="flex items-center gap-1">
              {loading && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
              {clearable && hasValue && !loading && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4"
                  onClick={handleClear}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  isOpen && "rotate-180",
                )}
              />
            </div>
          </button>

          {/* Hidden native select for form submission */}
          <select
            name={name}
            value={value || (multiple ? [] : "")}
            multiple={multiple}
            required={required}
            onChange={() => {
              onChange();
            }}
            className="sr-only"
            tabIndex={-1}
          >
            <option value="">Select...</option>
            {options?.map((option, index) => (
              <option key={index} value={option.value || option?.iso2}>
                {option.label || option?.name}
              </option>
            ))}
          </select>

          {isOpen &&
            (menuPortalTarget ? (
              createPortal(
                <div
                  ref={dropdownRef}
                  className={cn(
                    "fixed z-[99999] bg-white text-black border border-border rounded-md shadow-xl origin-top",
                    isClosing ? "animate-dropdown-out" : "animate-dropdown-in",
                  )}
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {searchable && (
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search options..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                          className="pl-8"
                        />
                      </div>
                    </div>
                  )}

                  <div className="py-1 max-h-60 overflow-auto">
                    {filteredOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        {searchTerm
                          ? "No options found"
                          : "No options available"}
                      </div>
                    ) : (
                      filteredOptions?.map((option, index) => (
                        <div
                          key={index}
                          className={cn(
                            "relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm transition-colors duration-100 ease-out hover:bg-logo2 hover:text-white",
                            isSelected(option.value) &&
                              "bg-primary text-primary-foreground",
                            option.disabled && "pointer-events-none opacity-50",
                          )}
                          onClick={() =>
                            !option.disabled && handleOptionSelect(option)
                          }
                        >
                          <span className="flex-1">{option.label}</span>
                          {multiple && isSelected(option.value) && (
                            <Check className="h-4 w-4" />
                          )}
                          {option.description && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {option.description}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>,
                menuPortalTarget,
              )
            ) : (
              <div
                className={cn(
                  "absolute z-50 w-full mt-1 bg-white text-black border border-border rounded-md shadow-md origin-top",
                  isClosing ? "animate-dropdown-out" : "animate-dropdown-in",
                )}
              >
                {searchable && (
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search options..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="pl-8"
                      />
                    </div>
                  </div>
                )}

                <div className="py-1 max-h-60 overflow-auto">
                  {filteredOptions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      {searchTerm ? "No options found" : "No options available"}
                    </div>
                  ) : (
                    filteredOptions?.map((option, index) => (
                      <div
                        key={index}
                        className={cn(
                          "relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm transition-colors duration-100 ease-out hover:bg-logo2 hover:text-white",
                          isSelected(option.value) &&
                            "bg-primary text-primary-foreground",
                          option.disabled && "pointer-events-none opacity-50",
                        )}
                        onClick={() =>
                          !option.disabled && handleOptionSelect(option)
                        }
                      >
                        <span className="flex-1">{option.label}</span>
                        {multiple && isSelected(option.value) && (
                          <Check className="h-4 w-4" />
                        )}
                        {option.description && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
        </div>

        {description && !error && (
          <p className="text-sm text-muted-foreground mt-2">{description}</p>
        )}
        {error && <p className="text-sm text-destructive mt-2">{error}</p>}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
